const ENTITY = /&(#\d+|#x[0-9a-f]+|[a-z][a-z0-9]*);/gi;
// Entities that matter for splitting; parse5 decodes them before the style parser runs
const ENTITY_CHARS: Record<string, string> = {
  '&quot;': '"',
  '&#34;': '"',
  '&#x22;': '"',
  '&apos;': "'",
  '&#39;': "'",
  '&#x27;': "'",
  '&semi;': ';',
  '&#59;': ';',
  '&#x3b;': ';',
};
const STYLE_ATTRIBUTE = /(\sstyle\s*=\s*)(["'])([\s\S]*?)\2/gi;
const DECLARATION = /^\s*([^:\s][^:]*?)\s*:\s*(\S[\s\S]*?)\s*$/;

// What the style parser will see: relevant entities decoded, the rest removed
const decodeForCheck = (css: string) => css.replace(ENTITY, (entity) => ENTITY_CHARS[entity.toLowerCase()] ?? '');

/**
 * Splits CSS declarations on top-level ';', keeping ones inside quotes and parentheses.
 */
const splitDeclarations = (css: string) => {
  const parts: string[] = [];
  let current = '';
  let depth = 0;
  let quote: string | null = null;

  for (let i = 0; i < css.length; i++) {
    const entity = css[i] === '&' ? css.slice(i).match(new RegExp(`^${ENTITY.source}`, 'i'))?.[0] : undefined;
    const token = entity ?? css[i];
    const char = entity ? ENTITY_CHARS[entity.toLowerCase()] ?? '' : css[i];
    i += token.length - 1;

    if (quote) {
      if (char === quote) {
        quote = null;
      }
    } else if (char === '"' || char === "'") {
      quote = char;
    } else if (char === '(') {
      depth++;
    } else if (char === ')') {
      depth = Math.max(0, depth - 1);
    } else if (char === ';' && depth === 0 && !entity) {
      parts.push(current);
      current = '';
      continue;
    }
    current += token;
  }
  parts.push(current);

  return parts;
};

const normalizeStyle = (css: string) =>
  splitDeclarations(css)
    .map((declaration) => declaration.match(DECLARATION))
    // Drop what browsers ignore anyway (empty values, stray text) and values with ';' inside
    // quotes/url(), which interweave-ssr's style parser cannot handle
    .filter((match): match is RegExpMatchArray => !!match && !decodeForCheck(match[2]).includes(';'))
    .map(([, property, value]) => `${property}: ${value}`)
    .join('; ');

/**
 * interweave-ssr parses inline styles with `style-parser`, which throws on declarations that
 * browsers silently drop (e.g. `color:;`), failing the whole server render. Normalize them first.
 */
export const sanitizeInlineStyles = (html: string) =>
  html.replace(STYLE_ATTRIBUTE, (_match, prefix: string, quote: string, css: string) => {
    return `${prefix}${quote}${normalizeStyle(css)}${quote}`;
  });
